import { format } from "date-fns";
import {useCallback, useMemo, useRef} from "react";
import DatePicker from "react-datepicker";
import {useFieldState, useFormApi} from "informed";
import GeneralConstants from "../../../constants/general.constants";

const normalizeDate = (v) => {
    if (!v) return null;              // handles undefined, null, empty string
    if (v === "") return null;        // handles undefined, null, empty string
    if (v instanceof Date) return v;  // already a Date
    const d = new Date(v);      // try to parse string/number
    return isNaN(d) ? null : d;       // guard against invalid date strings
};

const fromTodayAtTime = ({
    addDays = 0,
    atHours = 7,
    atMinutes = 0,
}) => {
    const d = new Date();
    d.setDate(d.getDate() + addDays);
    d.setHours(atHours, atMinutes, 0, 0);
    return d;
}

const sevenAM = (addDays = 0) => {
    return fromTodayAtTime({ addDays, atHours: 7 });
};

const fourPM = (addDays = 0) => {
    return fromTodayAtTime({ addDays, atHours: 16 });
}

const outputFormat = "y-MM-dd HH:mm";

const ProjectFormDateRangeInput = ({ onChange = () => {}, label, startDateField, endDateField }) => {
    const formApi = useFormApi();
    const startFieldDate = useFieldState(startDateField);
    const endFieldDate = useFieldState(endDateField);

    const normalizedStartDate = useMemo(() => normalizeDate(startFieldDate?.value), [startFieldDate?.value, endFieldDate?.value]);
    const normalizedEndDate = useMemo(() => normalizeDate(endFieldDate?.value), [endFieldDate?.value, startFieldDate?.value]);

    const startDateRef = useRef();
    const endDateRef = useRef();

    const handleFromDateChange = (date) => {
        let cleanDate = (date) ? format(date, outputFormat) : null;
        formApi.setValue(startDateField, cleanDate);
        onChange({ start: date || null, end: normalizedEndDate });
        startDateRef.current.setOpen(false);

        // when selecting a from-date, we want to set focus to end-date.
        // if the end-date is before the start-date, we want to set the end-date to the start-date + 1 day.
        if (
            date && (
                normalizedEndDate && normalizedEndDate < date ||
                normalizedEndDate === null
            )
        ) {
            const newEndDate = date;
            newEndDate.setHours(16, 0, 0, 0);
            formApi.setValue(endDateField, format(newEndDate, outputFormat));
            endDateRef.current.setPreSelection(newEndDate);
        }

        if (date) setTimeout(() => {
            endDateRef.current.setFocus();
        }, 250);
    };

    const handleToDateChange = (date) => {
        let cleanDate = (date) ? format(date, outputFormat) : null;
        formApi.setValue(endDateField, cleanDate);
        onChange({ start: normalizedStartDate || null, end: date });
        endDateRef.current.setOpen(false);
    };

    return (
        <div>
            <label className="input-description">
                {label}
            </label>
            <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <DatePicker
                    ref={startDateRef}
                    selectsStart
                    locale="nb-NO"
                    showTimeSelect
                    showWeekNumbers
                    timeIntervals={15}
                    shouldCloseOnSelect={true}
                    endDate={normalizedEndDate}
                    startDate={normalizedStartDate}
                    selected={normalizedStartDate || null}
                    placeholderText={GeneralConstants.from}
                    dateFormat={GeneralConstants.date_format}
                    openToDate={normalizedStartDate || sevenAM()}
                    onChange={handleFromDateChange}
                    onCalendarOpen={() => {
                        if (! normalizedStartDate) { // when the date is not set, we default it to tomorrow at 7 am
                            const sevenAMTomorrow = sevenAM(1);
                            startDateRef.current.setPreSelection(sevenAMTomorrow);
                            formApi.setValue(startDateField, format(sevenAMTomorrow, outputFormat));
                        }
                    }}
                />

                <span style={{ margin: "0 5px", color: "gray", }}>–</span>

                <DatePicker
                    ref={endDateRef}
                    selectsEnd
                    locale="nb-NO"
                    showTimeSelect
                    showWeekNumbers
                    timeIntervals={15}
                    shouldCloseOnSelect={true}
                    endDate={normalizedEndDate}
                    minDate={normalizedStartDate}
                    startDate={normalizedStartDate}
                    selected={normalizedEndDate || null}
                    placeholderText={GeneralConstants.to}
                    dateFormat={GeneralConstants.date_format}
                    openToDate={normalizedEndDate || fourPM(1)}
                    onChange={handleToDateChange}
                    onCalendarOpen={() => { // when the date is not set, we default it to two days from now at 4 pm
                        endDateRef.current.setPreSelection(null);

                        if (! normalizedEndDate) {
                            const fourPMInTwoDays = normalizedStartDate || fourPM(1);

                            if (normalizedStartDate) {
                                fourPMInTwoDays.setHours(16);
                            }

                            endDateRef.current.setPreSelection(fourPMInTwoDays);
                            formApi.setValue(endDateField, format(fourPMInTwoDays, outputFormat));
                        }
                    }}
                />
            </div>
        </div>
    )
}

export default ProjectFormDateRangeInput;